import { useCallback, useEffect, useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useTheme } from "next-themes";
import { ArrowRight, Check, Mail, Smartphone } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { confirmPhoneCode, phoneError, sendPhoneCode } from "@/lib/firebasePhone";
import { Field, PrimaryButton, inputCls } from "@/components/shared/ui";
import { StepActions, StepTitle } from "@/components/applicant/QuestionStep";
import { ErrorBox } from "@/components/applicant/stageUi";
import { PassLabel } from "@/components/applicant/JourneyPanel";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/** Step 1 and 2 before the application starts: name, email and phone, plus the robot check. */
const DetailsForm = ({ data, token, setData }) => {
  const c = data.contact || {};
  const [form, setForm] = useState({ full_name: c.full_name || "", email: c.email || "", phone: c.phone || "" });
  const [captcha, setCaptcha] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const widget = useRef(null);
  const { resolvedTheme } = useTheme();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      setData((await api.post(`/a/${token}/contact`, { ...form, turnstile_token: captcha })).data);
    } catch (err) {
      setError(errorText(err));
      widget.current?.reset();
      setCaptcha("");
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="rise" data-testid="contact-form">
      <StepTitle sub={data.phone_check ? "Before you start, we'll send a code to your email and your phone to check they're yours." : "Before you start, we'll email you a code to check the address is yours."}>Your details</StepTitle>
      <div className="mt-8 flex flex-col gap-5">
        <Field label="Full name, as on your ID">
          <input className={inputCls} autoComplete="name" value={form.full_name} onChange={set("full_name")} required data-testid="contact-name-input" />
        </Field>
        <Field label="Email address">
          <input className={inputCls} type="email" autoComplete="email" value={form.email} onChange={set("email")} required data-testid="contact-email-input" />
        </Field>
        <Field label="Mobile number">
          <input className={inputCls} type="tel" autoComplete="tel" placeholder="07700 900123" value={form.phone} onChange={set("phone")} required data-testid="contact-phone-input" />
        </Field>
        <div data-testid="contact-captcha">
          <Turnstile ref={widget} siteKey={SITE_KEY} onSuccess={setCaptcha} onExpire={() => setCaptcha("")} onError={() => setCaptcha("")}
            options={{ theme: resolvedTheme === "dark" ? "dark" : "light", size: "flexible" }} />
        </div>
      </div>
      {error && <ErrorBox className="mt-4" testid="contact-error">{error}</ErrorBox>}
      <StepActions>
        <PrimaryButton type="submit" size="xl" className="w-full" isLoading={busy} disabled={!captcha} testid="contact-submit">{data.phone_check ? "Send my codes" : "Send my code"} <ArrowRight /></PrimaryButton>
      </StepActions>
    </form>
  );
};

const CodeField = ({ icon: Icon, label, to, verified, value, onChange, onResend, testid }) => (
  <div className="rounded-xl bg-background p-4 ring-1 ring-border" data-testid={`${testid}-box`}>
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="truncate text-sm text-muted-foreground">{to}</p>
      </div>
      {verified && <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success" data-testid={`${testid}-verified`}><Check className="size-3" strokeWidth={3} /> Confirmed</span>}
    </div>
    {!verified && (
      <div className="mt-3 flex items-center gap-3">
        <input className={`${inputCls} max-w-[180px] tracking-[0.3em]`} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000"
          value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} data-testid={`${testid}-input`} />
        <button type="button" className="text-sm font-medium underline underline-offset-4" onClick={onResend} data-testid={`${testid}-resend`}>Send a new code</button>
      </div>
    )}
  </div>
);

const CodesForm = ({ data, token, setData }) => {
  const c = data.contact;
  const phoneOn = data.phone_check;
  const [codes, setCodes] = useState({ email_code: "", phone_code: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const confirmation = useRef(null);
  const started = useRef(false);

  const textPhone = useCallback(async () => {
    setError("");
    try {
      confirmation.current = await sendPhoneCode(c.phone, "phone-recaptcha");
      return true;
    } catch (err) {
      setError(phoneError(err));
      return false;
    }
  }, [c.phone]);

  useEffect(() => {
    if (!phoneOn || c.phone_verified || started.current) return;
    started.current = true;
    textPhone();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setNote("");
    try {
      let phoneToken = "";
      if (!c.phone_verified && codes.phone_code) {
        if (!confirmation.current) throw new Error("Please send a new text message code first.");
        try {
          phoneToken = await confirmPhoneCode(confirmation.current, codes.phone_code);
        } catch (err) {
          throw new Error(phoneError(err));
        }
      }
      setData((await api.post(`/a/${token}/contact/verify`, { email_code: codes.email_code, phone_token: phoneToken })).data);
    } catch (err) {
      setError(err.response ? errorText(err) : err.message);
      api.get(`/a/${token}`).then((r) => setData(r.data)).catch(() => {});
    }
    setBusy(false);
  };
  const resend = async (channel) => {
    setError("");
    setNote("");
    if (channel === "phone") {
      if (await textPhone()) setNote("New text message code sent.");
      return;
    }
    try {
      setData((await api.post(`/a/${token}/contact/resend`, { channel })).data);
      setNote("New email code sent.");
    } catch (err) {
      setError(errorText(err));
    }
  };
  const ready = (c.email_verified || codes.email_code.length === 6) && (!phoneOn || c.phone_verified || codes.phone_code.length === 6);

  return (
    <form onSubmit={verify} className="rise" data-testid="codes-form">
      <StepTitle sub={phoneOn ? "Enter the 6-digit codes we just sent you. They expire in 10 minutes." : "Enter the 6-digit code we just emailed you. It expires in 10 minutes."}>{phoneOn ? "Check your codes" : "Check your email"}</StepTitle>
      <div className="mt-8 flex flex-col gap-3">
        <CodeField icon={Mail} label="Email code" to={c.email} verified={c.email_verified} value={codes.email_code}
          onChange={(v) => setCodes({ ...codes, email_code: v })} onResend={() => resend("email")} testid="code-email" />
        {phoneOn && <CodeField icon={Smartphone} label="Text message code" to={c.phone} verified={c.phone_verified} value={codes.phone_code}
          onChange={(v) => setCodes({ ...codes, phone_code: v })} onResend={() => resend("phone")} testid="code-phone" />}
      </div>
      <div id="phone-recaptcha" />
      {note && <p className="mt-4 text-sm text-muted-foreground" data-testid="codes-note">{note}</p>}
      {error && <ErrorBox className="mt-4" testid="codes-error">{error}</ErrorBox>}
      <StepActions>
        <PrimaryButton type="submit" size="xl" className="w-full" isLoading={busy} disabled={!ready} testid="codes-submit">Confirm <ArrowRight /></PrimaryButton>
        <button type="button" className="text-sm text-muted-foreground underline underline-offset-4" onClick={() => setData({ ...data, contact: { ...c, sent_at: null } })} data-testid="codes-change-details">
          Change my details
        </button>
      </StepActions>
    </form>
  );
};

/** Standard checks before the application: they don't move the plane. Only once both codes pass does the journey begin. */
export const ContactGate = ({ data, token, setData }) => (
  <div className="mx-auto max-w-xl md:pt-6" data-testid="contact-gate">
    <PassLabel>{data.let.agency_name} · {data.let.address}</PassLabel>
    <div className="mt-4">
      {data.contact?.sent_at ? <CodesForm data={data} token={token} setData={setData} /> : <DetailsForm data={data} token={token} setData={setData} />}
    </div>
  </div>
);
